import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { FirestoreService } from '../../../../services/firestore/firestore.service';
import { SharedService } from '../../../../services/shared/shared.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { SocialMediaAccount } from '../../../../models/socialMediaAccount';
import { CompanyInfo } from '../../../../models/companyInfo';
import { SocialMediaPlatform } from '../../../../models/socialMediaPlatform';

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.css'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CommonModule
  ],
  standalone: true
})
export class SettingsComponent implements OnInit, OnDestroy {
  socialMediaAccounts: SocialMediaAccount[] = [];
  companyInfo: CompanyInfo | null = null;
  socialMediaForm: FormGroup;
  companyInfoForm: FormGroup;
  isEditMode = false;
  currentSocialMediaId: string | null = null;
  showSocialMediaModal = false;
  platforms = Object.values(SocialMediaPlatform);

  private subscriptions: Subscription = new Subscription();

  constructor(
    private firestoreService: FirestoreService,
    private fb: FormBuilder,
    public sharedService: SharedService,
    private spinner: NgxSpinnerService
  ) {
    this.socialMediaForm = this.fb.group({
      platform: ['', Validators.required],
      url: ['', Validators.required]
    });

    this.companyInfoForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      address: ['', Validators.required],
      phoneNumber: ['', Validators.required]
    });
  }

  ngOnInit() {
    this.loadSocialMediaAccounts();
    this.loadCompanyInfo();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  loadSocialMediaAccounts() {
    const sub = this.firestoreService.getAll('socialMedia').subscribe({
      next: (accounts) => {
        this.socialMediaAccounts = accounts;
      },
    });
    this.subscriptions.add(sub);
  }

  loadCompanyInfo() {
    const sub = this.firestoreService.getById('companyInfo', 'main').subscribe({
      next: (info) => {
        this.companyInfo = info || null;
        if (info) {
          this.companyInfoForm.patchValue({
            email: info.email || '',
            address: info.address || '',
            phoneNumber: info.phoneNumber || ''
          });
        }
      },
    });
    this.subscriptions.add(sub);
  }

  openSocialMediaModal(account?: SocialMediaAccount) {
    this.isEditMode = !!account;
    this.currentSocialMediaId = account?.id || null;

    if (account) {
      this.socialMediaForm.patchValue({
        platform: account.platform,
        url: account.url
      });
    } else {
      this.socialMediaForm.reset();
    }

    this.showSocialMediaModal = true;
  }

  saveSocialMedia() {
    if (this.socialMediaForm.valid) {
      this.spinner.show();
      const data: SocialMediaAccount = this.socialMediaForm.value;
      const operation = this.isEditMode && this.currentSocialMediaId
        ? this.firestoreService.update('socialMedia', this.currentSocialMediaId, data)
        : this.firestoreService.add('socialMedia', data);

      const sub = operation.subscribe({
        next: () => {
          this.sharedService.handleSuccess(
            this.isEditMode ? 'Social-Media-Konto erfolgreich aktualisiert' : 'Social-Media-Konto erfolgreich hinzugefügt'
          );
          this.closeModal();
        },
        error: (error) => {
          this.sharedService.handleError(error);
        }
      });
      this.subscriptions.add(sub);
    } else {
      this.sharedService.handleError('Bitte füllen Sie alle erforderlichen Felder aus');
    }
  }

  deleteSocialMedia(id: string) {
    this.spinner.show();
    const sub = this.firestoreService.delete('socialMedia', id).subscribe({
      next: () => {
        this.sharedService.handleSuccess('Social-Media-Konto erfolgreich gelöscht');
      },
      error: (error) => {
        this.sharedService.handleError(error);
      }
    });
    this.subscriptions.add(sub);
  }

  saveCompanyInfo() {
    if (this.companyInfoForm.valid) {
      this.spinner.show();
      const data: CompanyInfo = this.companyInfoForm.value;
      const sub = this.firestoreService.update('companyInfo', 'main', data).subscribe({
        next: () => {
          this.sharedService.handleSuccess('Unternehmensinformationen erfolgreich aktualisiert');
        },
        error: (error) => {
          this.sharedService.handleError(error);
        }
      });
      this.subscriptions.add(sub);
    } else {
      this.sharedService.handleError('Bitte füllen Sie alle erforderlichen Felder aus');
    }
  }

  closeModal() {
    this.showSocialMediaModal = false;
    this.socialMediaForm.reset();
    this.isEditMode = false;
    this.currentSocialMediaId = null;
  }
}